<template>
    <span class="checkbox-wrapper">
        <input type="checkbox"
               ref="$input"
               :class="`form__field form__field--checkbox ${(this.def ? `form__field--${this.def} --${this.def} js-${this.def}` : '' )}`"
               :name="def" :id="def"
               :checked="internalValue"
               :disabled="disabled"
               :tabindex="tabIndex"
               @input="onInput"
               @change="onChange"
               @click="onChange"
               @keydown="$emit('keydown', $event)"
               @keypress="$emit('keypress', $event)"
               @keyup="$emit('keyup', $event)"
               @keyup.enter="onEnter"
               @keyup.space="onSpace"
               @mousedown="$emit('mousedown', $event)"
               @mouseover="$emit('mouseover', $event)"
               @mouseup="$emit('mouseup', $event)"
               @touchstart="$emit('touchstart', $event)"
               @touchend="$emit('touchend', $event)"
               @touchcancel="$emit('touchcancel', $event)">
    </span>
</template>

<script>

import _ from 'lodash'

import FormControlInput from './form-control-input'

export default {

    props: {
        def: {
            type: String,
            default: 'checkbox',
        },
        value: {
            type: Boolean,
            default: false,
        },
        indeterminate: {
            type: Boolean,
            default: false,
        },
    },

    mixins: [
        FormControlInput,
    ],

    watch: {

        indeterminate(newValue) {
            this.$refs.$input.indeterminate = newValue
        },
    },

    methods: {

        /**
         * Enhances the event data with additional properties.
         *
         * @param {Object} $event The event data.
         * @param {boolean} forceToggleChecked Force the checked value to use the inverse of teh current state.
         */
        _enhanceEvent($event, forceToggleChecked = false) {
            $event.abortInLabel = true
            if (!$event.target) $event.target = { checked: !this.internalValue }
            if (forceToggleChecked) $event.target.checked = !this.internalValue
        },

        /**
         * Toggle checked state and bubble events.
         *
         * @param {string} fromEventName The name of the source event.
         * @param {Object} $fromEvent The source event.
         */
        _toggleAndTriggerCoreEvents(fromEventName, $fromEvent) {
            this.internalValue = $fromEvent.target.checked
            this.$emit('change', this.internalValue)
            if (!this.inputTriggers)
                this.$emit('input', this.internalValue)

            if ($fromEvent && (fromEventName !== 'change' || fromEventName !== 'input'))
                this.$emit(fromEventName, this.internalValue)
        },

        /**
         * Event that fires on change of the control value.
         *
         * Debouncing: This is debounced slower to ensure that change fires after input so it can trigger an input
         * if input events are not triggered by the browser to maintain browser event consistency.
         *
         * @param {Object} $event The event data.
         */
        onChange: _.debounce(function($event) {
            /* eslint-disable no-invalid-this */
            this._enhanceEvent($event)
            this._toggleAndTriggerCoreEvents('change', $event)
            $event.preventDefault()
            /* eslint-enable no-invalid-this */
        }, 100),

        /**
         * Event that fires on pressing enter on the control.
         *
         * @param {Object} $event The event data.
         */
        onEnter($event) {
            this._enhanceEvent($event, true)
            this.inputTriggers = false
            this._toggleAndTriggerCoreEvents('keyup.enter', $event)
            $event.preventDefault()
        },

        /**
         * Event that fires on pressing enter on the control.
         *
         * @param {Object} $event The event data.
         */
        onSpace($event) {
            this._enhanceEvent($event)
            this._toggleAndTriggerCoreEvents('keyup.space', $event)
            $event.preventDefault()
        },

        /**
         * Event that fires on inputing into a control.
         *
         * Debouncing: This is debounced to minimise triggers for an average typing speed.
         * Browser Note: Some browsers (EdgeHTML) do not trigger input events for some controls. Where this is
         * the case the 'change' event will fire an input event if input has not fired.
         *
         * @param {Object} $event The event data.
         */
        onInput($event) {
            this.inputTriggers = true
            this.internalValue = $event.target.checked
            this.$emit('input', this.internalValue)
            $event.preventDefault()
        },
    },
}

</script>


<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.

.form {

    @include element('field') {

        @include modifier('checkbox')
        {
            display: inline-block;
            width: auto;
        }
    }
}

</style>
